import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom'
import Onboarding from './pages/Onboarding'
import Welcome from './pages/Welcome'
import Register from './pages/Register'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import VerifyEmail from './pages/VerifyEmail'
import PhoneVerify from './pages/PhoneVerify'
import Flowers from './pages/Flowers'
import SetupSuccess from './pages/SetupSuccess'
import Subscription from './pages/Subscription'
import Address from './pages/Address'
import { SubscriptionProvider } from './context/SubscriptionContext'
import { AddressProvider } from './context/AddressContext'
import MobileLayout from './components/MobileLayout'
import SubscriptionManagement from './pages/SubscriptionManagement'
import QRPayment from './pages/QRPayment'
import PaymentRedirect from './pages/PaymentRedirect'
import Me from './pages/Me'
import BillingHistory from './pages/BillingHistory'
import RefundHistory from './pages/RefundHistory'
import RefundRequest from './pages/RefundDeliveryRequest'
import { Toaster } from 'react-hot-toast'
import { useEffect, lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from './services'
// Import analytics service
import { analyticsService } from './services/analytics'
// Import gift subscription pages
import GiftCodeRedemption from './pages/GiftCodeRedemption'
// Import new pages
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import Contact from './pages/Contact'
import DeliveryRange from './pages/DeliveryRange'
import DeliveryActivity from './pages/DeliveryActivity'
import DeliveryPage from './pages/DeliveryPage'
import SpecialRewardPage from './pages/SpecialReward'
import UpcomingServices from './pages/UpcomingServices'
import SubscriptionsList from './pages/SubscriptionsList'
import GoogleCallback from './pages/GoogleCallback'
// Import PageTitle component for title management
import PageTitle from './components/PageTitle'
import ReferralProgram from './pages/ReferralProgram'
 
import ConnectLine from './pages/ConnectLine'
import LineCallback from './pages/LineCallback'
 
import RenewalPage from './pages/RenewalPage'
// Import GoogleAnalyticsProvider
import GoogleAnalyticsProvider from './components/GoogleAnalyticsProvider'
import Fruits from './pages/Fruits'
import CatGrass from './pages/CatGrass'
import NotFound from './pages/NotFound'
import ScrollToTop from './components/ScrollToTop'
// Import optimized images to ensure they're included in the build
import './assets/images/index'

// Lazy load the payment-related components
const StripeProvider = lazy(() => import('./components/StripeProvider'));
const StripePayment = lazy(() => import('./pages/StripePayment'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const GiftSubscription = lazy(() => import('./pages/GiftSubscription'));
const RewardSubscription = lazy(() => import('./pages/RewardSubscription'));
const RewardSuccess = lazy(() => import('./pages/RewardSuccess'));

// Create a wrapper component for Onboarding
function OnboardingWrapper() {
  const navigate = useNavigate()
  
  useEffect(() => {
    // Check if user is already logged in
    if (authService.isAuthenticated()) {
      navigate('/flowers')
      return
    }
  }, [navigate])
  
  const handleOnboardingContinue = () => {
    // Instead of using navigate directly, we'll use the onContinue prop
    // to navigate to the welcome page
    window.location.href = '/welcome'
  }

  return <Onboarding onContinue={handleOnboardingContinue} />
}

// Create a wrapper for payment pages that need Stripe
function StripeWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="flex justify-center items-center min-h-screen">Loading payment system...</div>}>
      <StripeProvider>
        {children}
      </StripeProvider>
    </Suspense>
  );
}

// Track page views with Google Analytics
function TrackPageView() {
  const location = useLocation();
  
  useEffect(() => {
    analyticsService.pageView(location.pathname);
  }, [location]);
  
  return null;
}

function App() {
  // Add token validation on app initialization
  useEffect(() => {
    // Initialize Google Analytics
    analyticsService.init();
    
    const token = localStorage.getItem('authToken');
    
    // Only validate if a token exists and we're not on the login/register page
    if (token && 
        !window.location.pathname.includes('/login') && 
        !window.location.pathname.includes('/register') &&
        !window.location.pathname.includes('/reset-password') && 
        !window.location.pathname.includes('/verify-email')) {
      
      // We'll only check if token is not empty - no segment check
      // The server's validateSession function will properly validate the token
      if (!token.trim()) {
        console.log('Empty token detected, clearing authentication');
        localStorage.removeItem('authToken');
        localStorage.removeItem('lastActivityTime');
        
        // Redirect to login only if on a protected page
        if (!window.location.pathname.includes('/welcome') &&
            !window.location.pathname.includes('/forgot-password') &&
            !window.location.pathname.includes('/reset-password') &&
            !window.location.pathname.includes('/verify-email') &&
            window.location.pathname !== '/') {
          window.location.href = '/login';
        }
      }
    }
  }, []);
  
  return (
    <Router>
      <GoogleAnalyticsProvider measurementId="AW-17057672020">
        <SubscriptionProvider>
          <AddressProvider>
            <MobileLayout>
              <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
              <ScrollToTop />
              <TrackPageView />
              <Routes>
                <Route path="/" element={<><PageTitle titleKey="pages.onboarding.title" defaultTitle="Welcome to Piika" /><OnboardingWrapper /></>} />
                <Route path="/welcome" element={<><PageTitle titleKey="pages.welcome.title" defaultTitle="Welcome" /><Welcome /></>} />
                <Route path="/register" element={<><PageTitle titleKey="pages.register.title" defaultTitle="Register" /><Register /></>} />
                <Route path="/login" element={<><PageTitle titleKey="pages.login.title" defaultTitle="Login" /><Login /></>} />
                <Route path="/forgot-password" element={<><PageTitle titleKey="pages.forgotPassword.title" defaultTitle="Forgot Password" /><ForgotPassword /></>} />
                <Route path="/reset-password" element={<><PageTitle titleKey="pages.resetPassword.title" defaultTitle="Reset Password" /><ResetPassword /></>} />
                <Route path="/verify-email" element={<><PageTitle titleKey="pages.verifyEmail.title" defaultTitle="Verify Email" /><VerifyEmail /></>} />
                <Route path="/phone-verify" element={<><PageTitle titleKey="pages.phoneVerify.title" defaultTitle="Verify Phone" /><PhoneVerify /></>} />
                <Route path="/home" element={<><PageTitle titleKey="pages.home.title" defaultTitle="Home" /><Flowers /></>} />
                <Route path="/flowers" element={<><PageTitle titleKey="pages.flowers.title" defaultTitle="Flowers" /><Flowers /></>} />
                <Route path="/fruits" element={<><PageTitle titleKey="pages.fruits.title" defaultTitle="Fruits" /><Fruits /></>} />
                <Route path="/cat-grass" element={<><PageTitle titleKey="catGrass.pageTitle" defaultTitle="Cat Grass" /><CatGrass /></>} />
                {/* Redirect routes for singular forms */}
                <Route path="/flower" element={<Navigate to="/flowers" replace />} />
                <Route path="/fruit" element={<Navigate to="/fruits" replace />} />
                <Route path="/setup-success" element={<><PageTitle titleKey="pages.setupSuccess.title" defaultTitle="Setup Complete" /><SetupSuccess /></>} />
                <Route path="/subscription" element={<><PageTitle titleKey="pages.subscription.title" defaultTitle="Subscription" /><Subscription /></>} />
                <Route path="/subscription-management" element={<><PageTitle titleKey="pages.subscriptionManagement.title" defaultTitle="Manage Subscription" /><SubscriptionManagement /></>} />
                <Route path="/address" element={<><PageTitle titleKey="pages.address.title" defaultTitle="Delivery Address" /><Address /></>} />
                <Route path="/qr-payment" element={<><PageTitle titleKey="pages.qrPayment.title" defaultTitle="QR Payment" /><QRPayment /></>} />
                
                {/* Payment-related routes wrapped with StripeProvider */}
                <Route path="/stripe-payment" element={
                  <StripeWrapper>
                    <PageTitle titleKey="pages.stripePayment.title" defaultTitle="Payment" />
                    <Suspense fallback={<div>Loading payment page...</div>}>
                      <StripePayment />
                    </Suspense>
                  </StripeWrapper>
                } />
                
                <Route path="/payment-success" element={
                  <StripeWrapper>
                    <PageTitle titleKey="pages.paymentSuccess.title" defaultTitle="Payment Success" />
                    <Suspense fallback={<div>Loading...</div>}>
                      <PaymentSuccess />
                    </Suspense>
                  </StripeWrapper>
                } />
                
                <Route path="/payment-redirect" element={<><PageTitle titleKey="pages.paymentRedirect.title" defaultTitle="Processing Payment" /><PaymentRedirect /></>} />
                <Route path="/me" element={<><PageTitle titleKey="pages.me.title" defaultTitle="My Account" /><Me /></>} />
                <Route path="/billing-history" element={<><PageTitle titleKey="pages.billingHistory.title" defaultTitle="Billing History" /><BillingHistory /></>} />
                <Route path="/refund-history" element={<><PageTitle titleKey="pages.refundHistory.title" defaultTitle="Refund History" /><RefundHistory /></>} />
                <Route path="/refund-delivery/:deliveryId" element={<><PageTitle titleKey="pages.refund.title" defaultTitle="Request Refund" /><RefundRequest /></>} />
                
                <Route path="/gift-subscription" element={
                  <StripeWrapper>
                    <PageTitle titleKey="pages.giftSubscription.title" defaultTitle="Gift Subscription" />
                    <Suspense fallback={<div>Loading gift subscription...</div>}>
                      <GiftSubscription />
                    </Suspense>
                  </StripeWrapper>
                } />
                
                <Route path="/gift" element={<><PageTitle titleKey="pages.giftRedemption.title" defaultTitle="Redeem Gift" /><GiftCodeRedemption /></>} />
                <Route path="/gift/:code" element={<><PageTitle titleKey="pages.giftRedemption.title" defaultTitle="Redeem Gift" /><GiftCodeRedemption /></>} />
                {/* New routes */}
                <Route path="/terms" element={<><PageTitle titleKey="pages.terms.title" defaultTitle="Terms of Service" /><Terms /></>} />
                <Route path="/privacy" element={<><PageTitle titleKey="pages.privacy.title" defaultTitle="Privacy Policy" /><Privacy /></>} />
                <Route path="/contact" element={<><PageTitle titleKey="pages.contact.title" defaultTitle="Contact Us" /><Contact /></>} />
                <Route path="/contact-us" element={<><PageTitle titleKey="pages.contact.title" defaultTitle="Contact Us" /><Contact /></>} />
                <Route path="/delivery-range" element={<><PageTitle titleKey="pages.deliveryRange.title" defaultTitle="Delivery Range" /><DeliveryRange /></>} />
                <Route path="/subscriptions/:subscriptionId" element={<><PageTitle titleKey="pages.subscriptionDetails.title" defaultTitle="Subscription Details" /><SubscriptionManagement /></>} />
                <Route path="/subscriptions" element={<><PageTitle titleKey="pages.subscriptionsList.title" defaultTitle="My Subscriptions" /><SubscriptionsList /></>} />
                <Route path="/renewal/:subscriptionId" element={<><PageTitle titleKey="pages.renewal.title" defaultTitle="Renew Subscription" /><RenewalPage /></>} />
                <Route path="/delivery-activity" element={<><PageTitle titleKey="pages.deliveryActivity.title" defaultTitle="Delivery Activity" /><DeliveryActivity /></>} />
                <Route path="/delivery/:id" element={<><PageTitle titleKey="pages.deliveryDetails.title" defaultTitle="Delivery Details" /><DeliveryPage /></>} />
                <Route path="/referral-program" element={<><PageTitle titleKey="pages.referralProgram.title" defaultTitle="Referral Program" /><ReferralProgram /></>} />
                <Route path="/special-reward" element={<><PageTitle titleKey="pages.specialReward.title" defaultTitle="Special Reward" /><SpecialRewardPage /></>} />
                {/* LINE */}
                <Route path="/connect-line" element={<><PageTitle titleKey="account.connectLine" defaultTitle="Connect with LINE" /><ConnectLine /></>} />
                <Route path="/line-callback" element={<><PageTitle titleKey="account.lineCallback" defaultTitle="LINE Authentication" /><LineCallback /></>} />
                
                <Route path="/reward-subscription" element={
                  <StripeWrapper>
                    <PageTitle titleKey="pages.rewardSubscription.title" defaultTitle="Reward Subscription" />
                    <Suspense fallback={<div>Loading reward subscription...</div>}>
                      <RewardSubscription />
                    </Suspense>
                  </StripeWrapper>
                } />
                
                <Route path="/reward-success" element={
                  <StripeWrapper>
                    <PageTitle titleKey="pages.rewardSuccess.title" defaultTitle="Reward Success" />
                    <Suspense fallback={<div>Loading...</div>}>
                      <RewardSuccess />
                    </Suspense>
                  </StripeWrapper>
                } />
                
                <Route path="/upcoming-services" element={<><PageTitle titleKey="pages.upcomingServices.title" defaultTitle="Upcoming Services" /><UpcomingServices /></>} />
                <Route path="/google-callback" element={<><PageTitle titleKey="pages.googleCallback.title" defaultTitle="Google Authentication" /><GoogleCallback /></>} />
                
                {/* Catch-all route for 404 */}
                <Route path="*" element={<><PageTitle titleKey="pages.notFound.title" defaultTitle="Page Not Found" /><NotFound /></>} />
              </Routes>
            </MobileLayout>
          </AddressProvider>
        </SubscriptionProvider>
      </GoogleAnalyticsProvider>
    </Router>
  )
}

export default App